import React from 'react'
import memoizee from 'memoizee'
import {
  validateCreateMissionDto,
  maybeGetSuggestionText,
  convertCreateMissionDtoToMission,
  getDateFromStrings,
} from './../../RosterView/util'
import { createLowerCaseHashMapFromArray } from './../../../../Utils/Array'
import {
  toLocaleNonAmericanDateString,
  getWeekNumber,
  browserSafeNewDate,
  dropTimeZoneFromDate,
  get24HourTime,
  getShortWeekDay,
  formatToLocalDate,
  instantiateLocalDate,
} from './../../../../shared/libraries/utils/date'
import { downloadStringAsFile } from './../../../../Utils/Misc'
import { isDollars, isNumber } from '../../../../Utils/Validators/csv'
import {
  MISSION_ENUM_TYPES,
  MISSION_STATUS_ENUM,
} from '../../../../shared/constants'
import {
  getDisplayedWeekNumber,
  numberInStringSort,
  getWeeksFilterOptions,
  defaultMissionFilters,
  getOptions,
} from '../../../../Utils/Table'
import { calculateTimeOfDay } from '../../../../Utils/Mission'
import { normalizeGraphQLConnection } from '../../../../shared/graphql/utils'
import { StatusLabel } from '../../../../Components/StatusLabel'

const formatToFixedNumber = (number, options = 2) =>
  number ? Number(Number(number).toFixed(options)) : 0

const createHash = memoizee(createLowerCaseHashMapFromArray)

export const convertMissionDtoToMission = function(
  dto,
  { locations, ambassadors, cityPlans, type },
) {
  var ambassadorsHash = {}
  ambassadors.forEach(
    a =>
      (ambassadorsHash[
        a.firstName.toLowerCase() + ' ' + a.lastName.toLowerCase()
      ] = a.id),
  )
  var cityPlansHash = {}

  cityPlans.forEach(c => (cityPlansHash[c.city.name.toLowerCase()] = c.id))

  var data = {
    ...convertCreateMissionDtoToMission(dto, { locations }),
    userId: ambassadorsHash[dto['AMBASSADOR_NAME'].toLowerCase()],
    cityPlanId: cityPlansHash[dto['CITY_NAME'].toLowerCase()],
    checkInTime: instantiateLocalDate(
      formatToLocalDate(getDateFromStrings(dto['DATE'], dto['CHECKIN_TIME'])),
      cityPlans[0].city.timezone,
    ),
    checkoutTime: instantiateLocalDate(
      formatToLocalDate(getDateFromStrings(dto['DATE'], dto['CHECKOUT_TIME'])),
      cityPlans[0].city.timezone,
    ),
    flyerCode: dto['FLYER_CODE'],
    tag: dto['TAG'],
    distributionCost: Number(dto['DISTRIBUTION_COST']),
    surgePayout: Number(dto['PAYOUT']),
    printingCostPerFlyer: Number(dto['PRINTING_COST_PER_FLYER']),
    checkoutFlyerQuantity: Number(dto['CHECKOUT_FLYER_QUANTITY']),
    // These missions should all be historic
    status: MISSION_STATUS_ENUM.COMPLETED,
    type,
    // LB:
    boundary: [{ latitude: 0, longitude: 0 }],
    boundaryColour: dto['BOUNDARY_COLOR'],
  }

  if (dto['BONUS_ON_CONVERSION']) {
    data.bonusOnConversion = dto['BONUS_ON_CONVERSION']
  }

  if (dto['PAY_HOURLY']) {
    data.payHourly = convertBoolean(dto['PAY_HOURLY'])
  }

  if (dto['PAYOUT_RATIO']) {
    data.payoutRatio = dto['PAYOUT_RATIO']
  }

  return data
}

export const validateMissionDto = (
  dto,
  { locationNames, cityNames, ambassadorNames },
) => {
  // Do the same validation as when creating a new mission
  validateCreateMissionDto(dto, { locationNames })

  if (!dto['CITY_NAME']) throw new Error("Missing the 'CITY_NAME' column.")

  if (!dto['AMBASSADOR_NAME'])
    throw new Error("Missing the 'AMBASSADOR_NAME' column.")

  if (!dto['FLYER_CODE']) throw new Error('Missing flyer code.')

  if (!dto['DISTRIBUTION_COST']) throw new Error('Missing distribution cost.')

  if (isDollars(dto['DISTRIBUTION_COST']) === false)
    throw new Error(
      'Distribution cost is not valid, expects a dollar amount with cents as two decimal places. e.g. 104.00',
    )

  // Is reduced by the surge ratio if checkin time isn't within the surge time
  if (!dto['PAYOUT']) throw new Error('Missing payout.')

  if (
    dto['PAYOUT'] !== undefined &&
    dto['PAYOUT'] !== null &&
    isDollars(dto['PAYOUT']) === false
  )
    throw new Error(
      'Payout is not valid, expects a dollar amount with cents as two decimal places. e.g. 57.00',
    )

  //if (!dto['CHECKIN_DATE']) throw new Error('Missing checkin date.')

  if (!dto['CHECKIN_TIME']) throw new Error('Missing checkin time.')

  //if (!dto['CHECKOUT_DATE']) throw new Error('Missing checkout date.')

  if (!dto['CHECKOUT_TIME']) throw new Error('Missing checkout time.')

  if (!dto['CHECKOUT_FLYER_QUANTITY'])
    throw new Error('Missing checkout flyer quantity.')

  if (!dto['PRINTING_COST_PER_FLYER'])
    throw new Error('Missing printing cost per flyer.')

  // TODO Validate the PRINTING_COST_PER_FLYER as numeric
  if (isNumber(dto['PRINTING_COST_PER_FLYER']) === false)
    throw new Error('Printing cost per flyer is invalid.')

  // bonus on conversion is optional
  if (dto['BONUS_ON_CONVERSION'] !== undefined) {
    if (isDollars(dto['BONUS_ON_CONVERSION']) === false)
      throw new Error(
        'Bonus on conversion is not valid, expects a dollar amount with cents as two decimal places. e.g. 1.25',
      )
  }

  if (dto['PAYOUT_RATIO'] !== undefined)
    if (isNumber(dto['PAYOUT_RATIO']) === false)
      throw new Error('Payout ratio is invalid.')

  var cityNamesHash = createHash(cityNames)
  if (!cityNamesHash[dto['CITY_NAME'].toLowerCase()]) {
    var error = new Error(
      `City name ${dto['CITY_NAME']} does not match existing 
      city plans ${cityNames.join(', ')}.`.replace(/\s+/g, ' '),
    )
    error.suggestion = maybeGetSuggestionText(dto['CITY_NAME'], cityNames)
    throw error
  }

  var ambassadorsHash = createHash(ambassadorNames)

  if (!ambassadorsHash[dto['AMBASSADOR_NAME'].toLowerCase()]) {
    var error = new Error(
      `Could not find ambassador ${dto['AMBASSADOR_NAME']}.`,
    )
    error.suggestion = maybeGetSuggestionText(
      dto['AMBASSADOR_NAME'],
      ambassadorNames,
    )
    throw error
  }
}

export const downloadSampleCSV = function({
  locationNames,
  cityNames,
  ambassadorNames,
}) {
  var lastWeek = new Date()
  lastWeek.setDate(lastWeek.getDate() - 7)
  var exampleDate = toLocaleNonAmericanDateString(lastWeek)
  // Grab a random location
  if (locationNames.length) {
    var exampleIndex = Math.floor(Math.random() * locationNames.length)
    var exampleLocation = locationNames[exampleIndex]
  } else {
    var exampleLocation = 'Central Station'
  }

  if (cityNames.length) {
    var exampleIndex = Math.floor(Math.random() * cityNames.length)
    var exampleCityName = cityNames[exampleIndex]
  } else {
    var exampleCityName = 'Paris'
  }

  if (ambassadorNames.length) {
    var exampleIndex = Math.floor(Math.random() * ambassadorNames.length)
    var exampleAmbassadorName = ambassadorNames[exampleIndex]
  } else {
    var exampleAmbassadorName = 'Joe Blogs'
  }

  var data = {
    'DATE': exampleDate,
    'START_TIME': '08:00',
    'LOCATION': exampleLocation,
    'CITY_NAME': exampleCityName,
    'AMBASSADOR_NAME': exampleAmbassadorName,
    //'CHECKIN_DATE': exampleDate,
    'CHECKIN_TIME': '08:00',
    //'CHECKOUT_DATE': exampleDate,
    'CHECKOUT_TIME': '11:00',
    'FLYER_CODE': 'EXAMPLECODEABC',
    'CHECKOUT_FLYER_QUANTITY': '',
    'DISTRIBUTION_COST': '',
    'PAYOUT': '',
    'PRINTING_COST_PER_FLYER': '',
  }

  downloadStringAsFile(
    Object.keys(data).join(',') + '\n' + Object.values(data).join(','),
    `historic-data-example.csv`,
  )
}

/**
 * Converts a user string falsey/truthy value
 * @param {string} str
 * @returns {Boolean}
 */
export const convertBoolean = function(str) {
  switch (str.toLowerCase().trim()) {
    case 'no':
    case 'false':
    case '':
      return false
    default:
      return true
  }
}

/**
 * Calculate the bonus cost for a mission, computed from redux state props.
 * @param {string} mission
 * @returns {number}
 */
export const calculateBonusCostForGQLMission = mission => {
  // Filter out those conversions which won't be paid
  if (mission.bonusPerConversion && mission.bonusRedemptionPeriod) {
    const cutOff = new Date(mission.dateTime)
    cutOff.setDate(cutOff.getDate() + mission.bonusRedemptionPeriod)
    const conversions = mission.conversions.nodes.filter(c => {
      return c.metric.isPrimary && c.timestamp < cutOff.toISOString()
    })
    return (mission.bonusPerConversion * conversions.length) / 100
  } else {
    return 0
  }
}

/**
 * Calculates permit cost for each mission based on:
 * If campaign has permit cost set
 * If mission needs permit
 * If that week contains any missions that need a permit
 *
 * @param {number|string} missionId
 * @param {HashMap} hashMaps.missions - The mission that permit cost will be calculated for
 * @param {HashMap} hashMaps.cityPlan - City plan in order to
 * calculate how much missions needs permit cost in this roster
 * @param {HashMap} hashMaps.permitCosts - All permit costs
 * @param {HashMap} hashMaps.missions - HashMap of missions in order to get mission object
 * when looping over city plan missions to count missions that need permit cost
 * @param {HashMap} hashMaps.locations - HashMap of locations in order to get mission's location
 *
 * @return {Number} - Permit cost for the mission
 *
 * @example
 *
 * const hashMaps = {
 *   cityPlans: {
 *     2: {
 *       permitCostIds:[ "1111", "2222", ],
 *       missionIds:[ "1111", "2222", ]
 *     },
 *   locations: {
 *     32: {
 *       hasPermit: true,
 *     },
 *   },
 *   missions: {
 *     1111: {
 *       id: 1,
 *       location: 44,
 *       dateTime: '2019-07-21T10:00:00+10:00',
 *     },
 *   },
 *   permitCosts: {
 *     1111: {
 *       startDate: '2019-07-21T10:00:00+10:00',
 *       cost: 44,
 *     },
 *   }
 * }
 *
 * calculatePermitCostForMission(1, hashMaps)
 *
 */
export const calculatePermitCostForGQLMission = ({
  mission,
  cityPlan,
  campaignMissions,
}) => {
  if (
    cityPlan.permitCosts.nodes.length === 0 ||
    mission.type === MISSION_ENUM_TYPES.LETTERBOX
  ) {
    return 0
  }

  const missionDateTimeObj = new Date(mission.dateTime)
  const missionWeekNumber = getWeekNumber(missionDateTimeObj)

  const permitCostsByWeekMap = cityPlan.permitCosts.nodes.reduce(
    (accMap, costItem) => {
      const dateObj = new Date(costItem.startDate)
      const weekNumber = getWeekNumber(dateObj)
      accMap.set(weekNumber, {
        permitCost: costItem.cost,
      })
      return accMap
    },
    new Map(),
  )

  if (
    !permitCostsByWeekMap.get(missionWeekNumber) ||
    permitCostsByWeekMap.get(missionWeekNumber).permitCost === 0
  ) {
    return 0
  }

  const missionsByWeekMap = campaignMissions.reduce((accMap, missionItem) => {
    const dateObj = new Date(missionItem.dateTime)
    const weekNumber = getWeekNumber(dateObj)
    const prevRecord = accMap.get(weekNumber)
    if (!prevRecord) {
      accMap.set(weekNumber, {
        permitCost: 0,
        missions: [missionItem],
      })
    } else {
      const prevMissions = prevRecord.missions ? prevRecord.missions : []
      accMap.set(weekNumber, {
        ...prevRecord,
        missions: [...prevMissions, missionItem],
      })
    }

    return accMap
  }, permitCostsByWeekMap)

  const {
    permitCost: weekPermitCostCents,
    missions: weekMissions,
  } = missionsByWeekMap.get(missionWeekNumber)

  const weekPermitCost = weekPermitCostCents / 100
  const weekMissionsAmount = weekMissions.length
  const { location } = mission

  const amountOfMissionsWithPermit = weekMissions.filter(mission => {
    return location.hasPermit
  }).length

  if (amountOfMissionsWithPermit === 0) {
    return weekPermitCost / weekMissionsAmount
  } else if (location.hasPermit && weekPermitCost > 0) {
    return weekPermitCost / amountOfMissionsWithPermit
  }

  return 0
}

/**
 * Gets all the data required to display on the master schedule, it also includes
 * additional information that will be shown when the data is exported.
 * @returns {Object}
 */
export const getMasterScheduleMissionExportData = ({
  mission,
  city,
  campaign,
  campaignMissions,
  cityPlan,
}) => {
  const { location, user: ba, metricResults, type, label } = mission
  const locationName = location ? location.name : '-'

  const locationDependingOnTypeMission =
    type === MISSION_ENUM_TYPES.LETTERBOX ? label : locationName

  // Get a date relative to the missions timezone
  const localDate = browserSafeNewDate(dropTimeZoneFromDate(mission.dateTime))
  var data = {
    // Needed for links
    id: mission.id,
    'City': city.name,
    'Week': getDisplayedWeekNumber(localDate),
    'Date': toLocaleNonAmericanDateString(localDate),
    'Time': get24HourTime(localDate),
    'Time of day': calculateTimeOfDay(
      mission.dateTime,
      city.lunchCheckInTime,
      city.afternoonCheckInTime,
    ),
    'Day of week': getShortWeekDay(localDate.getDay()),
    'Location': locationDependingOnTypeMission,
    'BA': ba ? `${ba.firstName} ${ba.lastName}` : '-',
    'Code': mission.flyerCodeV2 ? mission.flyerCodeV2.code : '-',
    'Tag': mission.flyerCodeV2 ? mission.flyerCodeV2.tag : '-',
    'Flyers distributed': mission.totalFlyersDistributed || '-',
    'Status': (() => {
      if (mission.status == 'UNDER_REVIEW') return 'UNDER REVIEW'
      return mission.status
    })(),
    // Extra columns for exporting the data
    'Location Type': mission.location ? mission.location.type.name : '-',
    'Pack Size': mission.packSize,
    'Distribution Cost': mission.distributionCost,
    'Printing Cost': formatToFixedNumber(
      mission.totalFlyersDistributed * (mission.printingCostPerFlyerV2 / 100),
    ),
    'Cost Per Flyer': mission.costPerFlyer || '-',
  }

  data['Cash Bonus'] = calculateBonusCostForGQLMission(mission)

  // Figure out the permit cost
  data['Permit Cost'] = formatToFixedNumber(
    calculatePermitCostForGQLMission({
      mission,
      cityPlan,
      campaignMissions,
    }),
  )

  data['Hard Cost'] = formatToFixedNumber(
    formatToFixedNumber(data['Distribution Cost']) +
      formatToFixedNumber(data['Printing Cost']) +
      formatToFixedNumber(data['Cash Bonus']) +
      formatToFixedNumber(data['Permit Cost']),
  )

  data['Scans'] = mission.amountScans

  metricResults.nodes.forEach(result => {
    const { metric, value } = result
    const campaignMetricName = campaign.metrics.nodes.find(
      ({ id }) => id === metric.id,
    ).name
    data[campaignMetricName] = value
    data[`${campaignMetricName} CVR`] = value
      ? formatToFixedNumber(value / mission.totalFlyersDistributed, 4)
      : 0
    data[`Cost per ${campaignMetricName}`] =
      value == 0 ? '-' : formatToFixedNumber(data['Hard Cost'] / value)
  })

  // Link to the mission
  var { protocol, hostname, port } = window.location
  data['Link'] = `${protocol}//${hostname}${
    port ? ':' + port : ''
  }/dashboard/mission/${mission.id}`

  return data
}

/**
 * Gets all the data required to display on the master schedule
 * @returns {Object}
 */
export const getMasterScheduleMissionData = ({
  mission,
  campaign,
  campaignMissions,
}) => {
  const { location, user: ba, metricResults, type, label } = mission
  // Get a date relative to the missions timezone
  const localDate = browserSafeNewDate(dropTimeZoneFromDate(mission.dateTime))

  const locationName = location ? location.name : '-'

  const locationDependingOnTypeMission =
    type === MISSION_ENUM_TYPES.LETTERBOX ? label : locationName

  var data = {
    'City': mission.cityPlan.city.name,
    'Week': getDisplayedWeekNumber(localDate),
    'Date': toLocaleNonAmericanDateString(localDate),
    'Time': get24HourTime(localDate),
    'Time of day': mission.timeOfDay,
    'Day of week': getShortWeekDay(localDate.getDay()),
    'Spy':
      mission.spyInformation && mission.spyInformation.spy
        ? `${mission.spyInformation.spy.firstName} ${
            mission.spyInformation.spy.lastName
          }`
        : false,
    'Location': locationDependingOnTypeMission,
    'BA': ba ? `${ba.firstName} ${ba.lastName}` : '-',
    'Code': mission.flyerCode || '-',
    'Tag': mission.flyerCodeV2 ? mission.flyerCodeV2.tag : '-',
    'Flyers distributed': mission.totalFlyersDistributed || '-',
    'Status': (() => {
      let { status } = mission
      if (mission.status == 'UNDER_REVIEW') status = 'UNDER REVIEW'
      return (
        <StatusLabel
          isSpied={
            mission.spyInformation &&
            mission.spyInformation.spyDetails &&
            mission.spyInformation.spyDetails.checkInTime
          }
          status={status}
        />
      )
    })(),
    'Scans': mission.amountScans,
  }

  const printingCost =
    mission.totalFlyersDistributed * (mission.printingCostPerFlyer / 100)

  const hardCost =
    mission.distributionCost +
    printingCost +
    calculateBonusCostForGQLMission(mission)

  metricResults.nodes.forEach(result => {
    const { metric, value } = result
    data[metric.name] = value
    data[`Cost per ${metric.name}`] =
      value == 0 ? '-' : Number(hardCost / value).toFixed(2)
    data[`${metric.name} CVR`] = value
      ? Number(value / mission.totalFlyersDistributed).toFixed(2)
      : 0
  })

  return Object.values(data)
}

export const formatData = (data, campaign, formatter) => {
  const missions = normalizeGraphQLConnection(data.campaign.missions)
  return {
    data: missions.map(mission =>
      formatter({
        mission,
        campaign,
        campaignMissions: missions,
      }),
    ),
    actions: missions.map(mission => () => {
      window.open(`/dashboard/mission/${mission.id}`, '_blank')
    }),
    totalCount: data.campaign.missions.totalCount,
    cityPlans: data.campaign.cityPlans.nodes,
    missions,
  }
}

export const formatDataExport = (cityPlans, campaign, formatter) => ({
  cityPlans,
  missions: cityPlans.reduce((acc, cityPlan) => {
    const { city, missions } = cityPlan
    const flattenMissions = normalizeGraphQLConnection(missions)
    return [
      ...acc,
      ...flattenMissions.map(mission =>
        formatter({
          mission,
          city,
          campaign,
          campaignMissions: flattenMissions,
          cityPlan,
        }),
      ),
    ]
  }, []),
})

export const getFilters = ({ cities = [], locations = [] } = {}) => [
  {
    label: 'City',
    options: getOptions(cities),
  },
  {
    label: 'Location',
    options: getOptions(locations, numberInStringSort),
    sorted: false,
  },
  {
    label: 'Week',
    options: getWeeksFilterOptions(),
    sorted: false,
  },
  ...defaultMissionFilters,
]



// WEBPACK FOOTER //
// ./src/Containers/Campaigns/Page/MasterSchedule/util.js